/**
 * Indian Rummy (13-card Points Rummy) — server-authoritative rules engine, 2–6 players.
 *
 * Rules implemented (toggles live in {@link RummyConfig}):
 *  - Two 52-card decks plus two printed jokers (106 cards), 13 cards each. After the deal one card is turned up as the
 *    cut joker: every card of its rank is a wild joker for the deal (if a printed joker is turned, aces are wild).
 *    The next card starts the discard pile; the rest is the stock.
 *  - A turn is: draw one card (from the stock or the top of the discard pile), then discard one card — or declare.
 *  - Drop: before drawing you may leave the deal for a fixed penalty — `firstDrop` (20) if you have not drawn yet,
 *    `middleDrop` (40) afterwards. If everyone else drops, the last player wins.
 *  - Declare: after drawing, name the card to discard; the other 13 cards must form groups with at least one pure
 *    sequence and at least two sequences, every other card in a sequence or set (see ./melds.ts). An invalid declaration
 *    is rejected and the turn continues. The declarer scores 0.
 *  - Everyone else scores the points of the cards they could not group (capped at `maxPoints`, 80). Dropping scores the
 *    drop penalty. Wild jokers count 0, A/K/Q/J/10 count 10, the rest their pip value.
 *  - If the stock runs out the discard pile is reshuffled; after `maxReshuffles` the deal ends with no winner and every
 *    player scores their remaining points.
 *  - A match is `deals` deals; the lowest total wins and equal totals share a place.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';
import { analyzeRummyHand, rummyCardPoints, type RummyArrangement } from './melds.js';

export interface RummyConfig {
  /** Deals per match; the lowest total points after the last deal wins. */
  deals: number;
  firstDrop: number;
  middleDrop: number;
  /** Most points a single deal can cost a player who played it out. */
  maxPoints: number;
  maxReshuffles: number;
  roundEndDelayMs: number;
}

export type RummyPhase = 'DRAW' | 'DISCARD' | 'ROUND_END' | 'MATCH_END';
export type RummyDrop = 'first' | 'middle';

export interface RummyDealResult {
  deal: number;
  winner: number | null;
  reason: 'DECLARE' | 'LAST_PLAYER' | 'STOCK_OUT';
  /** Points each seat scored this deal. */
  points: number[];
  drops: (RummyDrop | null)[];
  /** Every hand that was still in play, revealed at the end of the deal (empty for seats that dropped). */
  hands: CardId[][];
  arrangements: (RummyArrangement | null)[];
  wildRank: number;
}

export interface RummyState {
  config: RummyConfig;
  players: string[];
  deal: number;
  dealer: number;
  phase: RummyPhase;
  hands: CardId[][];
  inHand: boolean[];
  drops: (RummyDrop | null)[];
  /** Has this seat drawn yet this deal (decides first vs middle drop)? */
  drawn: boolean[];
  turn: number;
  stock: CardId[];
  discard: CardId[];
  cutJoker: CardId;
  wildRank: number;
  reshuffles: number;
  totals: number[];
  dealResults: RummyDealResult[];
}

export type RummyAction =
  | { type: 'draw'; from: 'stock' | 'discard' }
  | { type: 'drop' }
  | { type: 'discard'; card: CardId }
  | { type: 'finish'; discard: CardId };

export interface RummyLegal {
  canDrawStock: boolean;
  canDrawDiscard: boolean;
  canDrop: boolean;
  /** Penalty for dropping now. */
  dropPoints: number;
  canDiscard: boolean;
  canFinish: boolean;
}

export interface RummyView {
  gameType: 'RUMMY';
  config: RummyConfig;
  seat: number | null;
  phase: RummyPhase;
  deal: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  inHand: boolean[];
  drops: (RummyDrop | null)[];
  cutJoker: CardId;
  wildRank: number;
  stockCount: number;
  discardTop: CardId | null;
  /** The whole discard pile, oldest first (public information). */
  discardPile: CardId[];
  totals: number[];
  dealResults: RummyDealResult[];
}

const DEFAULTS: RummyConfig = {
  deals: 2,
  firstDrop: 20,
  middleDrop: 40,
  maxPoints: 80,
  maxReshuffles: 2,
  roundEndDelayMs: 7000,
};

const HAND_SIZE = 13;

/** Rank that is wild for a given cut joker (aces when a printed joker is turned). */
export function rummyWildRank(cutJoker: CardId): number {
  const c = parseCard(cutJoker);
  return c.joker ? 14 : c.rank;
}

function nextInHand(state: RummyState, from: number): number {
  const n = state.players.length;
  for (let k = 1; k <= n; k++) {
    const s = (from + k) % n;
    if (state.inHand[s]) return s;
  }
  return from;
}

function dealCards(state: RummyState, rng: Rng, events: EngineEvent[]): void {
  const n = state.players.length;
  const deck = shuffle(buildDeck({ decks: 2, printedJokersPerDeck: 1 }), rng);
  state.hands = Array.from({ length: n }, (_, s) => sortHand(deck.slice(s * HAND_SIZE, (s + 1) * HAND_SIZE)));
  let k = n * HAND_SIZE;
  state.cutJoker = deck[k++]!;
  state.wildRank = rummyWildRank(state.cutJoker);
  state.discard = [deck[k++]!];
  state.stock = deck.slice(k);
  state.inHand = Array<boolean>(n).fill(true);
  state.drops = Array<RummyDrop | null>(n).fill(null);
  state.drawn = Array<boolean>(n).fill(false);
  state.reshuffles = 0;
  state.phase = 'DRAW';
  state.turn = (state.dealer + 1) % n;
  events.push({ type: 'dealt', deal: state.deal, dealer: state.dealer, cutJoker: state.cutJoker, wildRank: state.wildRank, discardTop: state.discard[0] });
}

function arrange(state: RummyState, seat: number): RummyArrangement {
  return analyzeRummyHand(state.hands[seat]!, state.wildRank).arrangement();
}

function endDeal(state: RummyState, winner: number | null, reason: RummyDealResult['reason'], events: EngineEvent[]): void {
  const cfg = state.config;
  const n = state.players.length;
  const arrangements: (RummyArrangement | null)[] = Array(n).fill(null);
  const points: number[] = [];
  for (let s = 0; s < n; s++) {
    if (state.drops[s]) {
      points.push(state.drops[s] === 'first' ? cfg.firstDrop : cfg.middleDrop);
    } else if (s === winner) {
      arrangements[s] = arrange(state, s);
      points.push(0);
    } else {
      arrangements[s] = arrange(state, s);
      points.push(Math.min(cfg.maxPoints, arrangements[s]!.deadwood));
    }
  }
  const result: RummyDealResult = {
    deal: state.deal,
    winner,
    reason,
    points,
    drops: [...state.drops],
    hands: state.hands.map((h, s) => (state.drops[s] ? [] : [...h])),
    arrangements,
    wildRank: state.wildRank,
  };
  state.dealResults.push(result);
  state.totals = state.totals.map((t, s) => t + points[s]!);
  events.push({ type: 'dealEnd', deal: state.deal, winner, reason, points, totals: [...state.totals] });
  if (state.deal >= cfg.deals) {
    state.phase = 'MATCH_END';
    events.push({ type: 'matchEnd', standings: lowestFirst(state.totals) });
  } else {
    state.phase = 'ROUND_END';
  }
}

function lowestFirst(totals: readonly number[]): Standing[] {
  const sorted = totals.map((score, seat) => ({ seat, score })).sort((a, b) => a.score - b.score);
  const out: Standing[] = [];
  sorted.forEach((s, i) => {
    const prev = out[i - 1];
    out.push({ seat: s.seat, score: s.score, place: prev && prev.score === s.score ? prev.place : i + 1 });
  });
  return out;
}

function dropPoints(state: RummyState, seat: number): number {
  return state.drawn[seat] ? state.config.middleDrop : state.config.firstDrop;
}

/** The card a stuck or idle player throws: the costliest card that is not a wild joker. */
function timeoutDiscard(state: RummyState, seat: number): CardId {
  const hand = state.hands[seat]!;
  return [...hand].sort((a, b) => rummyCardPoints(b, state.wildRank) - rummyCardPoints(a, state.wildRank) || parseCard(b).rank - parseCard(a).rank)[0]!;
}

export const rummyEngine: GameEngine<RummyConfig, RummyState, RummyAction, RummyView, RummyLegal> = {
  gameType: 'RUMMY',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('deals', c.deals, 1, 6);
    checkInt('firstDrop', c.firstDrop, 0, 80);
    checkInt('middleDrop', c.middleDrop, 0, 80);
    checkInt('maxPoints', c.maxPoints, 20, 200);
    checkInt('maxReshuffles', c.maxReshuffles, 0, 5);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: () => ({ min: 2, max: 6 }),

  createState(config, seats, rng) {
    assertEngine(seats.length >= 2 && seats.length <= 6, 'INVALID_CONFIG', 'Rummy needs 2 to 6 seats');
    const n = seats.length;
    const players = Array<string>(n).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < n && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: RummyState = {
      config,
      players,
      deal: 1,
      dealer: rng.int(n),
      phase: 'DRAW',
      hands: [],
      inHand: [],
      drops: [],
      drawn: [],
      turn: 0,
      stock: [],
      discard: [],
      cutJoker: '',
      wildRank: 14,
      reshuffles: 0,
      totals: Array<number>(n).fill(0),
      dealResults: [],
    };
    dealCards(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'draw' && (raw.from === 'stock' || raw.from === 'discard')) return { type: 'draw', from: raw.from };
    if (raw.type === 'drop') return { type: 'drop' };
    if (raw.type === 'discard' && typeof raw.card === 'string' && raw.card.length <= 8) return { type: 'discard', card: raw.card };
    if (raw.type === 'finish' && typeof raw.discard === 'string' && raw.discard.length <= 8) return { type: 'finish', discard: raw.discard };
    throw new EngineError('INVALID_ACTION', 'Unknown Rummy action');
  },

  applyAction(state, seat, action, rng) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && (state.phase === 'DRAW' || state.phase === 'DISCARD'), 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;

    if (action.type === 'draw') {
      assertEngine(state.phase === 'DRAW', 'INVALID_PHASE', 'You have already drawn');
      let card: CardId;
      if (action.from === 'discard') {
        assertEngine(state.discard.length > 0, 'ILLEGAL_MOVE', 'The discard pile is empty');
        card = state.discard.pop()!;
        events.push({ type: 'draw', seat, from: 'discard', card });
      } else {
        if (state.stock.length === 0) {
          if (state.reshuffles >= state.config.maxReshuffles || state.discard.length < 2) {
            endDeal(state, null, 'STOCK_OUT', events);
            return events;
          }
          const top = state.discard.pop()!;
          state.stock = shuffle(state.discard, rng);
          state.discard = [top];
          state.reshuffles++;
          events.push({ type: 'reshuffle', count: state.stock.length });
        }
        card = state.stock.pop()!;
        events.push({ type: 'draw', seat, from: 'stock' });
      }
      hand.push(card);
      state.drawn[seat] = true;
      state.phase = 'DISCARD';
      return events;
    }

    if (action.type === 'drop') {
      assertEngine(state.phase === 'DRAW', 'INVALID_PHASE', 'You can only drop before drawing');
      const pts = dropPoints(state, seat);
      state.drops[seat] = state.drawn[seat] ? 'middle' : 'first';
      state.inHand[seat] = false;
      // The dropped cards go back under the stock.
      state.stock.unshift(...hand);
      state.hands[seat] = [];
      events.push({ type: 'drop', seat, points: pts });
      const left = state.inHand.map((x, s) => (x ? s : -1)).filter((s) => s >= 0);
      if (left.length === 1) {
        endDeal(state, left[0]!, 'LAST_PLAYER', events);
        return events;
      }
      state.turn = nextInHand(state, seat);
      return events;
    }

    assertEngine(state.phase === 'DISCARD', 'INVALID_PHASE', 'Draw a card first');
    const card = action.type === 'discard' ? action.card : action.discard;
    assertEngine(hand.includes(card), 'INVALID_CARD', 'Card not in hand');

    if (action.type === 'finish') {
      const idx = hand.indexOf(card);
      assertEngine(analyzeRummyHand(hand, state.wildRank).isMade(idx), 'ILLEGAL_MOVE', 'Your cards do not form a valid hand yet');
      removeCards(hand, [card]);
      state.discard.push(card);
      events.push({ type: 'declare', seat, card });
      endDeal(state, seat, 'DECLARE', events);
      return events;
    }

    removeCards(hand, [card]);
    state.discard.push(card);
    events.push({ type: 'discard', seat, card });
    state.phase = 'DRAW';
    state.turn = nextInHand(state, seat);
    return events;
  },

  getActors(state) {
    return state.phase === 'DRAW' || state.phase === 'DISCARD' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    const mine = seat === state.turn && (state.phase === 'DRAW' || state.phase === 'DISCARD');
    const none: RummyLegal = { canDrawStock: false, canDrawDiscard: false, canDrop: false, dropPoints: 0, canDiscard: false, canFinish: false };
    if (!mine) return none;
    if (state.phase === 'DRAW') {
      return { ...none, canDrawStock: true, canDrawDiscard: state.discard.length > 0, canDrop: true, dropPoints: dropPoints(state, seat) };
    }
    return { ...none, canDiscard: true, canFinish: analyzeRummyHand(state.hands[seat]!, state.wildRank).isMadeAny() };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'DRAW') return { type: 'draw', from: 'stock' };
    return { type: 'discard', card: timeoutDiscard(state, seat) };
  },

  getView(state, seat) {
    return {
      gameType: 'RUMMY',
      config: state.config,
      seat,
      phase: state.phase,
      deal: state.deal,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      inHand: [...state.inHand],
      drops: [...state.drops],
      cutJoker: state.cutJoker,
      wildRank: state.wildRank,
      stockCount: state.stock.length,
      discardTop: state.discard.at(-1) ?? null,
      discardPile: [...state.discard],
      totals: [...state.totals],
      dealResults: state.dealResults.map((r) => ({ ...r, points: [...r.points], drops: [...r.drops], hands: r.hands.map((h) => [...h]) })),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'ROUND_END') {
      state.deal++;
      state.dealer = (state.dealer + 1) % state.players.length;
      dealCards(state, rng, events);
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return lowestFirst(state.totals);
  },

  getRoundsCompleted: (state) => state.dealResults.length,
};

